import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {GridSortItem} from '@mui/x-data-grid-pro';

import {clientsStore} from '@store/clients/clients.store';
import {ClientsFilters, ClientsPagination} from '@store/clients/clients.model';
import {getClientsDataSource} from '@store/clients/clients.requests';
import {GlobalStatus} from '@store/common/globalStatus.enum';
import {ManageOrganisation} from '@store/common/organisation.model';
import {Pagination} from '@store/common/pagination.model';
import {TableFilter} from '@store/common/tableFilter.model';

import {TableConfig} from '@constants/table.constants';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {OrganisationStatus} from '@store/common/organisation.enums';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class ClientsService {
  store = clientsStore;

  setStatus = (status?: GlobalStatus) => this.store.update((state) => ({...state, status}));
  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: ClientsFilters.initTableFilters()},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  searchClients = (search?: string): Observable<ClientsPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETClients(),
      params: {
        searchString: search || undefined,
        sortBy: 'organisation.name',
        orderBy: 'ASC',
        page: 1,
        limit: TableConfig.pagination,
        status: OrganisationStatus.ACTIVE
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsPagination>) => {
          return response.data;
        }),
      );
  };

  getClients = (): Observable<ClientsPagination> => {
    const {status, filters: {search, tableFilters}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClients(),
      params: {
        ...requestFilters,
        status,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsPagination>) => {
          return response.data;
        }),
        tap((clientsPagination) => {
          const paginationData = Pagination.toPaginationData(clientsPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(clientsPagination.data),
            upsertEntitiesById(clientsPagination.data.map((client) => client.id), {
              updater: (clientUi) => clientUi,
              creator: (clientId) => ({id: clientId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, clientsPagination.data.map((client) => client.id)),
            getClientsDataSource.setSuccess(),
          );
        }),
        getClientsDataSource.trackRequestStatus(),
      );
  };

  getClientsCSV = () => {
    const {status, filters: {search, tableFilters}, sort} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        status,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'contracts.xlsx');
        }),
      );
  };

  updateClientStatusInList = (clientId: number | string, status: GlobalStatus): Observable<ClientsPagination> => {
    return from(APIAxios({
      ...APIRoutes.PATCHClientStatus(clientId),
      data: {status},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClients()),
      );
  };

  createClient = (data: ManageOrganisation): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.POSTClient(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisation>) => {
          return response.data;
        }),
      );
  };

  deleteClientInList = (clientId: number | string): Observable<ClientsPagination> => {
    return from(APIAxios(APIRoutes.DELETEClient(clientId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getClients()),
      );
  };

  setSelectedClients = (clientIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (clientUI) => ({...clientUI, selected: clientIds.includes(clientUI.id)}),
        {ref: UIEntitiesRef},
      ),
    );
  };
}

export const clientsService = new ClientsService();
